import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const ThreeDCarousel = ({ images }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [fullscreenIndex, setFullscreenIndex] = useState(null);
  const containerRef = useRef(null);
  const lastScrollTime = useRef(0);

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Auto slide
  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length, isHovered]);

  // Scroll control
  useEffect(() => {
    const handleWheel = (e) => {
      const currentTime = new Date().getTime();
      if (currentTime - lastScrollTime.current > 600) {
        if (e.deltaY > 0) nextSlide();
        else if (e.deltaY < 0) prevSlide();
        lastScrollTime.current = currentTime;
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener("wheel", handleWheel);
      }
    };
  }, [images.length]);

  // 3D styling
  const getSlideStyle = (index) => {
    const isMobile = window.innerWidth < 768;
    const rotateAngle = isMobile ? 15 : 30;
    const xOffset = isMobile ? "30%" : "35%";

    if (index === activeIndex) {
      return {
        x: "0%",
        z: 0,
        rotateY: 0,
        scale: 1,
        opacity: 1,
        zIndex: 10,
        filter: "blur(0px)",
      };
    } else if (index === (activeIndex - 1 + images.length) % images.length) {
      return {
        x: `-${xOffset}`,
        z: -300,
        rotateY: rotateAngle,
        scale: 0.85,
        opacity: 0.3,
        zIndex: 5,
        filter: "blur(2px)",
      };
    } else if (index === (activeIndex + 1) % images.length) {
      return {
        x: xOffset,
        z: -300,
        rotateY: -rotateAngle,
        scale: 0.85,
        opacity: 0.3,
        zIndex: 5,
        filter: "blur(2px)",
      };
    } else {
      return {
        x: "0%",
        z: -500,
        rotateY: 0,
        scale: 0.7,
        opacity: 0,
        zIndex: 1,
        filter: "blur(10px)",
      };
    }
  };

  // Drag support
  const handleDragEnd = (event, info) => {
    if (info.offset.x > 50) prevSlide();
    else if (info.offset.x < -50) nextSlide();
  };

  return (
    <>
      <div className="container">
        <div
          ref={containerRef}
          className="relative w-full h-[380px] md:h-[580px] flex items-center justify-center overflow-hidden bg-[#0a0a0b] rounded-[40px] my-6 shadow-2xl border border-white/5"
          style={{ perspective: "1500px" }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="relative w-full max-w-[950px] h-full flex items-center justify-center transform-style-3d px-10">
            <AnimatePresence initial={false}>
              {images.map((img, index) => (
                <motion.div
                  key={index}
                  className="absolute w-full  aspect-video rounded-2xl overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] cursor-grab active:cursor-grabbing border border-white/5"
                  style={{
                    transformStyle: "preserve-3d",
                  }}
                  initial={getSlideStyle(index)}
                  animate={getSlideStyle(index)}
                  transition={{
                    duration: 0.7,
                    ease: [0.32, 0.72, 0, 1],
                  }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  onDragEnd={handleDragEnd}
                  onClick={() => {
                    if (index === activeIndex) {
                      setFullscreenIndex(index);
                    } else if (
                      index ===
                      (activeIndex - 1 + images.length) % images.length
                    ) {
                      prevSlide();
                    } else if (index === (activeIndex + 1) % images.length) {
                      nextSlide();
                    }
                  }}
                >
                  <div className="w-full h-full relative">
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full"
                      draggable="false"
                    />
                    {index !== activeIndex && (
                      <div className="absolute inset-0 bg-black/40 z-10" />
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-6 md:left-10 z-30 w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all duration-300"
            aria-label="Previous slide"
          >
            <FiChevronLeft size={20} />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-6 md:right-10 z-30 w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all duration-300"
            aria-label="Next slide"
          >
            <FiChevronRight size={20} />
          </button>

          {/* Pagination Dots */}
          <div className="absolute bottom-10 flex gap-2 z-30">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  idx === activeIndex ? "w-6" : "bg-white/20 w-1.5"
                }`}
                style={
                  idx === activeIndex
                    ? {
                        background:
                          "linear-gradient(90deg,rgba(128, 79, 252, 1) 0%, rgba(33, 112, 233, 1) 100%)",
                      }
                    : {}
                }
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* <AnimatePresence>
        {fullscreenIndex !== null && (
          <motion.div
            className="fixed inset-0 z-[999] bg-black/95 flex flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.img
              key={fullscreenIndex}
              src={images[fullscreenIndex]}
              alt="fullscreen"
              className="max-w-[95%] max-h-[75%] object-contain rounded-xl"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.3 }}
            />


            <button
              onClick={() => setFullscreenIndex(null)}
              className="absolute top-6 right-6 text-white text-3xl bg-white/10 px-4 py-1 rounded-lg hover:bg-white/20"
            >
              ✕
            </button>


            <button
              onClick={() =>
                setFullscreenIndex(
                  (prev) => (prev - 1 + images.length) % images.length,
                )
              }
              className="absolute left-6 top-1/2 -translate-y-1/2 text-white text-3xl bg-white/10 px-4 py-2 rounded-lg hover:bg-white/20"
            >
              ‹
            </button>

            <button
              onClick={() =>
                setFullscreenIndex((prev) => (prev + 1) % images.length)
              }
              className="absolute right-6 top-1/2 -translate-y-1/2 text-white text-3xl bg-white/10 px-4 py-2 rounded-lg hover:bg-white/20"
            >
              ›
            </button>


            <div className="w-full  mt-6 px-4 overflow-x-auto">
              <div className="flex gap-3 justify-center">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => setFullscreenIndex(idx)}
                    className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      idx === fullscreenIndex
                        ? "scale-110 shadow-[0_0_15px_rgba(128,79,252,0.6)]"
                        : "border-white/20 opacity-60 hover:opacity-100"
                    }`}
                    style={
                      idx === fullscreenIndex
                        ? { borderColor: "rgba(128, 79, 252, 1)" }
                        : {}
                    }
                  >
                    <img
                      src={img}
                      alt={`thumb-${idx}`}
                      className="w-20 h-14 object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence> */}
      <AnimatePresence>
        {fullscreenIndex !== null && (
          <motion.div
            className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex flex-col items-center justify-center px-4 py-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* MAIN CONTAINER */}
            <div className="relative flex items-center justify-center w-full max-w-7xl">
              {/* LEFT BUTTON */}
              <button
                onClick={() =>
                  setFullscreenIndex(
                    (prev) => (prev - 1 + images.length) % images.length,
                  )
                }
                className="
                  absolute left-2 md:-left-20 top-1/2 -translate-y-1/2
                  w-12 h-12 md:w-14 md:h-14
                  rounded-full
                  bg-white/10
                  backdrop-blur-xl
                  border border-white/20
                  text-white
                  text-3xl
                  flex items-center justify-center
                  hover:bg-[#2f72e5]
                  hover:scale-110
                  transition-all duration-300
                  shadow-2xl
                "
              >
                ‹
              </button>

              {/* IMAGE */}
              <motion.img
                key={fullscreenIndex}
                src={images[fullscreenIndex]}
                alt="fullscreen"
                className="
                  max-w-full
                  max-h-[80vh]
                  object-contain
                  rounded-3xl
                  shadow-[0_20px_80px_rgba(0,0,0,0.6)]
                "
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.3 }}
              />

              {/* RIGHT BUTTON */}
              <button
                onClick={() =>
                  setFullscreenIndex((prev) => (prev + 1) % images.length)
                }
                className="
                  absolute right-2 md:-right-20 top-1/2 -translate-y-1/2
                  w-12 h-12 md:w-14 md:h-14
                  rounded-full
                  bg-white/10
                  backdrop-blur-xl
                  border border-white/20
                  text-white
                  text-3xl
                  flex items-center justify-center
                  hover:bg-[#2f72e5]
                  hover:scale-110
                  transition-all duration-300
                  shadow-2xl
                "
              >
                ›
              </button>

              {/* CLOSE BUTTON */}
              <button
                onClick={() => setFullscreenIndex(null)}
                className="
                  absolute -top-16 right-0
                  w-12 h-12
                  rounded-full
                  bg-white/10
                  backdrop-blur-xl
                  border border-white/20
                  text-white
                  text-2xl
                  flex items-center justify-center
                  hover:bg-red-500
                  hover:rotate-90
                  transition-all duration-300
                  shadow-2xl
                "
              >
                ✕
              </button>
            </div>

            {/* THUMBNAILS */}
            <div className="w-full max-w-6xl mt-8 scrollbar-hide">
              <div className="flex gap-4 justify-center px-2">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => setFullscreenIndex(idx)}
                    className={`
                      cursor-pointer
                      rounded-2xl
                      overflow-hidden
                      border-2
                      shrink-0
                      transition-all duration-300
                      ${
                        idx === fullscreenIndex
                          ? "scale-110 border-[#804ffc] shadow-[0_0_25px_rgba(128,79,252,0.8)]"
                          : "border-white/10 opacity-60 hover:opacity-100"
                      }
                    `}
                  >
                    <img
                      src={img}
                      alt={`thumb-${idx}`}
                      className="w-24 h-16 md:w-32 md:h-20 object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ThreeDCarousel;
